import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Activity, Languages, UserPlus, DollarSign, 
  AlertTriangle, Wifi, WifiOff, Trash2, RefreshCw,
  FileText, Cpu, Zap
} from 'lucide-react';

const EVENT_CONFIG = {
  translation_completed: {
    icon: Languages,
    color: 'text-indigo-600 bg-indigo-100',
    label: 'Translation'
  },
  mtpe_completed: {
    icon: FileText,
    color: 'text-purple-600 bg-purple-100',
    label: 'MTPE'
  },
  cat_segment_translated: {
    icon: Cpu,
    color: 'text-blue-600 bg-blue-100',
    label: 'CAT Segment'
  },
  cat_batch_completed: {
    icon: Zap,
    color: 'text-cyan-600 bg-cyan-100',
    label: 'CAT Batch'
  },
  tqa_report_generated: {
    icon: FileText,
    color: 'text-amber-600 bg-amber-100',
    label: 'TQA Report'
  },
  user_registered: {
    icon: UserPlus,
    color: 'text-emerald-600 bg-emerald-100',
    label: 'New User'
  },
  user_login: {
    icon: Activity,
    color: 'text-blue-600 bg-blue-100',
    label: 'Login'
  },
  credits_purchased: {
    icon: DollarSign,
    color: 'text-green-600 bg-green-100',
    label: 'Wallet Top-up'
  },
  credits_deducted: {
    icon: DollarSign,
    color: 'text-amber-600 bg-amber-100',
    label: 'Deduction'
  },
  error_occurred: {
    icon: AlertTriangle,
    color: 'text-red-600 bg-red-100',
    label: 'Error'
  }
};

function EventItem({ event }) {
  const config = EVENT_CONFIG[event.type] || {
    icon: Activity,
    color: 'text-zinc-600 bg-zinc-100',
    label: event.type
  };
  
  const Icon = config.icon;
  const timestamp = new Date(event.timestamp).toLocaleTimeString();
  
  const renderDetails = () => {
    const data = event.data || {};
    
    switch (event.type) {
      case 'translation_completed':
        return (
          <span>
            <span className="font-medium">{data.user}</span>
            {' translated '}
            <span className="font-medium">{data.words}</span>
            {' words '}
            <span className="text-zinc-500">
              ({data.source_lang} → {data.target_lang})
            </span>
            {data.cache_hit && <span className="text-emerald-600 ml-1">[cached]</span>}
            {data.cost_inr > 0 && (
              <span className="text-amber-600 ml-1">-₹{data.cost_inr.toFixed(2)}</span>
            )}
          </span>
        );
        
      case 'mtpe_completed':
        return (
          <span>
            <span className="font-medium">{data.user}</span>
            {' refined '}
            <span className="font-medium">{data.words}</span>
            {' words '}
            <span className="text-zinc-500">({data.style})</span>
            {data.cost_inr > 0 && (
              <span className="text-amber-600 ml-1">-₹{data.cost_inr.toFixed(2)}</span>
            )}
          </span>
        );
        
      case 'user_registered':
        return (
          <span>
            <span className="font-medium">{data.user}</span>
            {' joined'}
            {data.bonus_credits && (
              <span className="text-emerald-600 ml-1">+₹{(data.bonus_credits * 0.10).toFixed(0)} bonus</span>
            )}
          </span>
        );
        
      case 'credits_purchased':
        return (
          <span>
            <span className="font-medium">{data.user}</span>
            {' topped up '}
            <span className="font-medium text-green-600">+₹{data.amount}</span>
          </span>
        );
        
      case 'error_occurred':
        return (
          <span>
            <span className="font-mono text-xs">{data.path}</span>
            {' - '}
            <span className="text-red-600">{data.status_code}</span>
            {': '}
            <span className="text-zinc-600">{data.message}</span>
          </span>
        );
        
      default:
        return <span>{JSON.stringify(data)}</span>;
    }
  };
  
  return (
    <div className="flex items-start gap-3 py-2 border-b border-zinc-100 last:border-0">
      <div className={`p-1.5 rounded-full ${config.color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">{config.label}</span>
          <span className="text-xs text-zinc-400">{timestamp}</span>
        </div>
        <p className="text-sm text-zinc-700 truncate">{renderDetails()}</p>
      </div>
    </div>
  );
}

export default function LiveActivityFeed({ 
  events = [], 
  isConnected, 
  onClear, 
  onReconnect,
  connectionCount = 0
}) {
  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-zinc-900">Live Activity</h3>
          {isConnected ? (
            <span className="flex items-center gap-1 text-xs text-emerald-600">
              <Wifi className="h-3 w-3" />
              Connected
              {connectionCount > 1 && (
                <span className="text-zinc-400">({connectionCount} admins)</span>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <WifiOff className="h-3 w-3" />
              Disconnected
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isConnected && (
            <Button variant="ghost" size="sm" onClick={onReconnect}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClear} disabled={events.length === 0}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      
      {/* Event List */}
      <div className="flex-1 overflow-y-auto px-4">
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-400 py-8">
            <Activity className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No activity yet</p>
            <p className="text-xs">Events will appear here in real-time</p>
          </div>
        ) : (
          <div className="py-2">
            {events.map((event) => (
              <EventItem key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 border-t bg-zinc-50 text-xs text-zinc-500">
        {events.length} events • Last 50 shown
      </div>
    </Card>
  );
}
